import { useState, useRef, useCallback, useEffect } from "react";
import {
  playUrlWithFallbacks,
  waitForPlayable,
  wireMediaElement,
} from "@/modules/common/utils/audio-playback.utils";

type AudioSource = Blob | string;

interface AudioPlaybackState {
  playingId: string | null;
  loadedId: string | null;
  currentTime: number;
  duration: number;
  isLoading: boolean;
  errors: Record<string, string>; // Changed from single error to per-ID errors
  playbackRate: number;
}

interface UseAudioPlaybackReturn {
  playingId: string | null;
  loadedId: string | null;
  currentTime: number;
  duration: number;
  isLoading: boolean;
  playbackRate: number;
  togglePlay: (id: string, source: AudioSource) => void;
  stopPlayback: () => void;
  cleanup: () => void;
  isPlaying: (id: string) => boolean;
  isLoaded: (id: string) => boolean;
  getCurrentTimeForId: (id: string) => number;
  getDurationForId: (id: string) => number;
  clearErrorForId: (id: string) => void;
  getErrorForId: (id: string) => string | null;
  seekTo: (id: string, time: number) => void;
  setPlaybackRate: (rate: number) => void;
}

/**
 * Custom hook for managing audio playback state with support for multiple audio sources.
 * Supports both Blob (for local recordings) and string URLs (for presigned URLs).
 */
const useAudioPlayback = (): UseAudioPlaybackReturn => {
  const [state, setState] = useState<AudioPlaybackState>({
    playingId: null,
    loadedId: null,
    currentTime: 0,
    duration: 0,
    isLoading: false,
    errors: {}, // Changed from error: null to errors: {}
    playbackRate: 1,
  });

  const audioRef = useRef<HTMLMediaElement | null>(null);
  const playbackTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearPlaybackTimer = useCallback(() => {
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
  }, []);

  const startPlaybackTimer = useCallback(() => {
    clearPlaybackTimer();
    playbackTimerRef.current = setInterval(() => {
      if (audioRef.current) {
        setState((prev) => ({
          ...prev,
          currentTime: audioRef.current?.currentTime || 0,
        }));
      }
    }, 100);
  }, [clearPlaybackTimer]);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    clearPlaybackTimer();
    setState((prev) => ({
      playingId: null,
      loadedId: null,
      currentTime: 0,
      duration: 0,
      isLoading: false,
      errors: {}, // Changed from error: null to errors: {}
      playbackRate: prev.playbackRate,
    }));
  }, [clearPlaybackTimer]);

  const togglePlay = useCallback(
    (id: string, source: AudioSource) => {
      // If this audio is currently playing, pause it
      if (state.playingId === id && audioRef.current) {
        audioRef.current.pause();
        clearPlaybackTimer();
        setState((prev) => ({ ...prev, playingId: null }));
        return;
      }

      // If this audio is loaded but paused, resume it
      if (state.loadedId === id && audioRef.current) {
        audioRef.current.play().catch(() => {
          setState((prev) => ({
            ...prev,
            playingId: null,
            errors: { ...prev.errors, [id]: "Failed to play audio." },
          }));
        });
        setState((prev) => {
          const newErrors = { ...prev.errors };
          delete newErrors[id]; // Clear error for this ID
          return { ...prev, playingId: id, errors: newErrors };
        });
        startPlaybackTimer();
        return;
      }

      // Otherwise load and play new audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        clearPlaybackTimer();
      }

      const isUrl = typeof source === "string";
      const audioSrc = isUrl ? source : URL.createObjectURL(source);

      // Clear error for this ID before loading (for both URL and Blob sources)
      setState((prev) => {
        const newErrors = { ...prev.errors };
        delete newErrors[id]; // Clear error for this ID when loading
        return {
          ...prev,
          isLoading: isUrl, // Show loading state only for URL sources
          errors: newErrors,
        };
      });

      let hasEnded = false;

      const handleEnded = () => {
        hasEnded = true;
        setState((prev) => ({ ...prev, playingId: null, currentTime: 0 }));
        clearPlaybackTimer();
      };

      // The strategy that wins returns its own element, already playing —
      // adopt it so seek/pause/rate act on what is actually audible.
      const onSuccess = (media: HTMLMediaElement) => {
        audioRef.current = media;
        media.playbackRate = state.playbackRate;
        media.onended = handleEnded;
        setState((prev) => ({
          ...prev,
          playingId: id,
          loadedId: id,
          currentTime: media.currentTime || 0,
          duration: Number.isFinite(media.duration) ? media.duration : 0,
          isLoading: false,
        }));
        startPlaybackTimer();
      };

      const onFail = (message: string) => {
        if (hasEnded) return;
        audioRef.current = null;
        clearPlaybackTimer();
        setState((prev) => ({
          ...prev,
          playingId: null,
          loadedId: null,
          isLoading: false,
          errors: { ...prev.errors, [id]: message },
        }));
      };

      if (isUrl) {
        // Try direct URL first (desktop / short mobile recordings).
        // If that fails, automatically falls back to blob fetch, then
        // video element, then WAV re-encode as a last resort.
        playUrlWithFallbacks(audioSrc, state.playbackRate)
          .then((media) => onSuccess(media))
          .catch(() => onFail("Recording expired or unavailable"));
      } else {
        // Local recording from microphone
        const a = new Audio();
        wireMediaElement(a, { playbackRate: state.playbackRate });
        a.src = audioSrc;
        audioRef.current = a;
        waitForPlayable(a)
          .then(() => onSuccess(a))
          .catch(() => onFail("Failed to load audio."));
      }
    },
    [state.playingId, state.loadedId, clearPlaybackTimer, startPlaybackTimer],
  );

  const cleanup = useCallback(() => {
    stopPlayback();
  }, [stopPlayback]);

  const isPlaying = useCallback(
    (id: string) => state.playingId === id,
    [state.playingId],
  );

  const isLoaded = useCallback(
    (id: string) => state.loadedId === id,
    [state.loadedId],
  );

  const getCurrentTimeForId = useCallback(
    (id: string) => {
      if (state.playingId === id || state.loadedId === id) {
        return state.currentTime;
      }
      return 0;
    },
    [state.playingId, state.loadedId, state.currentTime],
  );

  const getDurationForId = useCallback(
    (id: string) => {
      if (state.loadedId === id) {
        return state.duration;
      }
      return 0;
    },
    [state.loadedId, state.duration],
  );

  const clearErrorForId = useCallback((id: string) => {
    setState((prev) => {
      const newErrors = { ...prev.errors };
      delete newErrors[id];
      return { ...prev, errors: newErrors };
    });
  }, []);

  const getErrorForId = useCallback(
    (id: string) => {
      return state.errors[id] || null;
    },
    [state.errors],
  );

  const seekTo = useCallback(
    (id: string, time: number) => {
      if (!audioRef.current) return;
      if (state.loadedId !== id && state.playingId !== id) return;
      const clamped = Math.max(
        0,
        Math.min(time, audioRef.current.duration || time),
      );
      audioRef.current.currentTime = clamped;
      setState((prev) => ({ ...prev, currentTime: clamped }));
    },
    [state.loadedId, state.playingId],
  );

  const setPlaybackRate = useCallback((rate: number) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
    setState((prev) => ({ ...prev, playbackRate: rate }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    playingId: state.playingId,
    loadedId: state.loadedId,
    currentTime: state.currentTime,
    duration: state.duration,
    isLoading: state.isLoading,
    playbackRate: state.playbackRate,
    togglePlay,
    stopPlayback,
    cleanup,
    isPlaying,
    isLoaded,
    getCurrentTimeForId,
    getDurationForId,
    clearErrorForId,
    getErrorForId,
    seekTo,
    setPlaybackRate,
  };
};

export default useAudioPlayback;
